/**
 * 1.7.9 — who gets what, by kind (D-1.7.9-8): the volunteer's reminder and its answer box, and dropping out.
 *
 * A volunteer who said "I'm available" and holds a place gets the volunteers' reminder with their own link to the
 * same answer box, which now reads "I'm still coming / I can't make it anymore". Saying they cannot come any more
 * asks why first: one of four chips and a line (the line is required for "something else"). The reason is kept on
 * the volunteer's own RSVP line, the app's way — one `Dropped out: <why> | <line> | <when>` line in its
 * AdministrativeNotes (there is no column for it) — and it is what the event's Volunteers card and the quiet tally
 * on the volunteer's card read. Nothing here sends anything.
 */
import { z } from 'zod';
import { serviceDateKey } from './dates';

export const DROP_REASONS = ['work', 'family', 'unwell', 'other'] as const;
export type DropReason = (typeof DROP_REASONS)[number];
export const DROP_LINE_MAX = 120;
/** "Something else" needs at least this much of a line. */
export const DROP_LINE_MIN = 3;

/** What the answer box posts with "I can't make it anymore": `{ why, line }`. */
export const DropReasonSchema = z.object({ why: z.enum(DROP_REASONS), line: z.string().trim().max(DROP_LINE_MAX).optional().default('') });
export type DropReasonInput = z.input<typeof DropReasonSchema>;
export type DropReasonGiven = z.output<typeof DropReasonSchema>;

/** A reason that can be saved: one of the four, and for "something else" a line that says what. */
export const dropReasonOk = (r: { why?: string; line?: string } | null | undefined): r is DropReasonGiven =>
  !!r && (DROP_REASONS as readonly string[]).includes(r.why ?? '') && (r.why !== 'other' || (r.line ?? '').trim().length >= DROP_LINE_MIN);

/** The refusal's code (HTTP 400) and its words, in the person's language. */
export const REASON_REQUIRED = 'reason-required';
export const REASON_REQUIRED_WORDS = {
  en: 'Please tell us why you can’t make it — choose one, or write a line — and send again.',
  es: 'Por favor, díganos por qué ya no puede ir: elija una opción o escriba una línea, y envíe de nuevo.',
} as const;

/** The key of the line in the RSVP row's AdministrativeNotes. */
export const DROP_NOTE = 'Dropped out';

export interface DropOut {
  why: DropReason;
  line: string;
  /** When they said so (ISO). */
  at: string;
}

const clean = (s: string) => s.replace(/\s+/g, ' ').replace(/\|/g, '/').trim().slice(0, DROP_LINE_MAX);
/** `family | my sister is visiting | 2026-10-20T14:03:00.000Z` — a dash where no line was written. */
export const dropOutValue = (r: DropReasonGiven, at: Date) => `${r.why} | ${clean(r.line) || '-'} | ${at.toISOString()}`;
export function dropOutOf(value: string): DropOut | null {
  const parts = value.split(' | ');
  if (parts.length < 3) {
    // a line a hand wrote: `Dropped out: family`
    const why = value.trim().split(/\s/)[0] ?? '';
    return (DROP_REASONS as readonly string[]).includes(why) ? { why: why as DropReason, line: '', at: '' } : null;
  }
  const why = parts[0]!.trim();
  if (!(DROP_REASONS as readonly string[]).includes(why)) return null;
  const line = parts.slice(1, -1).join(' | ').trim();
  return { why: why as DropReason, line: line === '-' ? '' : line, at: parts[parts.length - 1]!.trim() };
}
/** The New York day (yyyyMMdd) someone dropped out; '' when the line carries no time. */
export const dropDayKey = (d: Pick<DropOut, 'at'>) => (d.at && !Number.isNaN(Date.parse(d.at)) ? serviceDateKey(new Date(d.at)) : '');

/** One line under the going list: "dropped out · Oct 20 · family". */
export interface DroppedLine extends DropOut {
  rsvpId: string;
  volunteerId: string;
  firstName: string;
  name: string;
  dayKey: string;
}

/** What the event's Volunteers card gets beside 1.7.7's `EventHelpers` (the same object carries both). */
export interface HelpersDropOuts {
  dropped: DroppedLine[];
  /** Places nobody holds now (Volunteers needed less those going), when someone dropped out. */
  open: number;
  /** Ready volunteers with an email who have said neither yes nor no — whom "Request again" would reach now. */
  toAskAgain: number;
}

/** The answer of Request again: 1.7.7's send result and the round it belongs to (sent back while any remain). */
export const RequestAgainSchema = z.object({ since: z.string().datetime().optional() });

/** The quiet tally on a volunteer's card — administrators and co-administrators only. */
export interface VolunteerTally {
  volunteerId: string;
  /** Events they said "I'm available" for (those they later dropped out of included). */
  available: number;
  /** Of those, the ones they were checked in at. */
  came: number;
  droppedOut: number;
  drops: { eventId: string; event: string; eventDayKey: string; dayKey: string; why: DropReason; line: string }[];
}

/** The reminder's one button, and the two answers of someone who holds a place. */
export const ANSWER_BUTTON = { en: 'Open my answer', es: 'Abrir mi respuesta' } as const;
export const STILL_BUTTONS = {
  en: { yes: 'I’m still coming', no: 'I can’t make it anymore' },
  es: { yes: 'Todavía voy', no: 'Ya no puedo ir' },
} as const;
